import React, { useState, useEffect, useContext } from "react";
import { LocalizationContext } from '../localization/LocalizationContext';
import { parseMultiLanguageDescription } from '../utils/multiLanguageParser';
import { fs, authentication, storage } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { signOut, getAuth } from "firebase/auth";
import '../css/index.css';
import { getDownloadURL, ref } from "firebase/storage";
import '../css/userpage.css';
import { useTranslation } from "react-i18next";
import Bar from "../elements/bar";
import AuthModal from "../components/AuthModal";
import { getUserCardNumber, getUserPhoneNumber } from "../utils/userUtils";
import { getWalletBalancesByCardNumber, syncMarketingConsent } from '../services/syrveLoyaltySync';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import { RESTAURANT_META, LOYALTY_RESTAURANTS } from '../config/restaurantMeta';
import OrderHistory from './DeliveryInterface/OrderHistory';
import AddressManager from '../components/AddressManager';
import {
    ArrowRight,
    BadgePercent,
    ChevronRight,
    CreditCard,
    Edit3,
    Heart,
    History,
    LogOut,
    MapPin,
    QrCode,
    Settings,
    Star,
    Truck,
    User,
    X,
} from 'lucide-react';

function UserPage({ handleXBarItemChange }) {
    const { t } = useTranslation();
    const { locale } = useContext(LocalizationContext);

    useEffect(() => {
        if (localStorage.getItem('forceLogout') === 'true') {
            localStorage.clear();
            localStorage.removeItem('forceLogout');
            window.location.reload();
        }
    }, []);

    const [lOGstatus, setLOGstatus] = useState(localStorage.getItem('LOGstatus'));
    const [postI, setPostI] = useState(localStorage.getItem('USER_POST'));
    const [nameI, setNameI] = useState('');
    const [profileImage, setProfileImage] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [coins, setCoins] = useState({});
    const [showBarcodeModal, setShowBarcodeModal] = useState(false);
    const [userCardNumber, setUserCardNumber] = useState('');
    const [showOrders, setShowOrders] = useState(false);
    const [showAddressManager, setShowAddressManager] = useState(false);
    const [showFavorites, setShowFavorites] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [settingsForm, setSettingsForm] = useState({ name: '', phone: '', email: '', personalId: '' });
    const [marketingOptIn, setMarketingOptIn] = useState(false);

    const handleMarketingToggle = async (e) => {
        const value = e.target.checked;
        setMarketingOptIn(value);
        const user = getAuth().currentUser;
        if (!user) return;
        try {
            await setDoc(doc(fs, 'users', user.uid), { marketingOptIn: value, updatedAt: new Date().toISOString() }, { merge: true });
            syncMarketingConsent(user.uid);
        } catch (error) {
            console.error('Error updating marketing preference:', error);
            setMarketingOptIn(!value);
        }
    };

    const handleAuthSuccess = (sessionData) => {
        setNameI(sessionData.fullName);
        setPostI(sessionData.userEmail);
        setLOGstatus('true');
        if (sessionData.cardNumber) {
            setUserCardNumber(sessionData.cardNumber);
        }
    };

    const handleOut = async () => {
        const auth = getAuth();
        try {
            await signOut(auth);
            localStorage.clear();
            sessionStorage.clear();
            document.cookie.split(";").forEach(function (c) {
                document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
            });
            window.location.href = '/';
        } catch (error) {
            console.error("Logout error:", error);
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/';
        }
    };

    useEffect(() => {
        const handleAuthResult = async () => {
            const userLogin = localStorage.getItem('USER_POST');
            const userId = localStorage.getItem('USER_ID');

            if (!userLogin && !userId) return;

            try {
                let userDocRef;
                let docSnap;

                if (userId) {
                    userDocRef = doc(fs, 'users', userId);
                    docSnap = await getDoc(userDocRef);
                }

                if (!docSnap || !docSnap.exists()) {
                    userDocRef = doc(fs, 'Users', userLogin);
                    docSnap = await getDoc(userDocRef);
                }

                if (docSnap.exists()) {
                    const userData = docSnap.data();

                    const fullName = userData.firstName && userData.lastName
                        ? `${userData.firstName} ${userData.lastName}`
                        : userData.name || '';

                    const userEmail = userData.email || userData.post || userData.mail || '';

                    localStorage.setItem('LOGstatus', 'true');
                    localStorage.setItem('USER_NAME', fullName);
                    localStorage.setItem('USER_POST', userEmail);

                    setNameI(fullName);
                    setPostI(userEmail);
                    setLOGstatus('true');
                    setPhoneNumber(getUserPhoneNumber(userData) || '');

                    if (userData.CardNumber || userData.cardNumber) {
                        setUserCardNumber(userData.CardNumber || userData.cardNumber);
                    }
                }
            } catch (error) {
                console.error("Error loading user data:", error);
            }
        };

        handleAuthResult();
    }, []);

    useEffect(() => {
        const fetchProfileImage = async () => {
            if (!postI) return;
            try {
                const storageRef = ref(storage, `USER_IMG/${postI}`);
                const imageUrl = await getDownloadURL(storageRef);
                setProfileImage(imageUrl);
            } catch (error) {
                setProfileImage(null);
            }
        };

        fetchProfileImage();
    }, [postI]);

    const fetchCardCoins = async () => {
        try {
            const user = authentication.currentUser;
            if (!user) return;

            const userRef = doc(fs, "users", user.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const userData = userSnap.data();
                const cardNum = getUserCardNumber(userData);

                if (cardNum) {
                    const phone = getUserPhoneNumber(userData) || getUserPhoneNumber(user);

                    const walletBalances = await getWalletBalancesByCardNumber(cardNum, phone);

                    if (walletBalances) {
                        setCoins(walletBalances);
                    } else {
                        const coinsRef = doc(fs, "coins", cardNum);
                        const coinsSnap = await getDoc(coinsRef);
                        if (coinsSnap.exists()) {
                            const formattedCoins = Object.fromEntries(
                                Object.entries(coinsSnap.data()).map(([store, value]) => [store, Number(value) || 0])
                            );
                            setCoins(formattedCoins);
                        } else {
                            setCoins({});
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error loading balances:", error);
            const user = authentication.currentUser;
            if (user) {
                const userRef = doc(fs, "users", user.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const userData = userSnap.data();
                    const cardNum = getUserCardNumber(userData);
                    if (cardNum) {
                        const coinsRef = doc(fs, "coins", cardNum);
                        const coinsSnap = await getDoc(coinsRef);
                        if (coinsSnap.exists()) {
                            const formattedCoins = Object.fromEntries(
                                Object.entries(coinsSnap.data()).map(([store, value]) => [store, Number(value) || 0])
                            );
                            setCoins(formattedCoins);
                        } else {
                            setCoins({});
                        }
                    }
                }
            }
        }
    };

    useEffect(() => {
        fetchCardCoins();
    }, []);

    const totalCoins = (coins && coins.fastdelivery != null)
        ? coins.fastdelivery
        : Object.values(coins || {}).reduce((total, coin) => total + coin, 0);

    const loyaltySummaryKeys = ['fastdelivery', 'aromiitaliani', 'nadimi', 'symposium', 'copacabana', 'poshnosh'];
    const loyaltyTotal = Object.values(coins || {}).reduce((total, coin) => total + (Number(coin) || 0), 0);

    return (
        <>
            <Bar onNavClick={handleXBarItemChange} activeItem={3} />

            {lOGstatus !== "true" && (
                <div className="fd-profile-auth-wrapper">
                    <AuthModal
                        isOpen={true}
                        onClose={() => handleXBarItemChange(0)}
                        onAuthSuccess={handleAuthSuccess}
                    />
                </div>
            )}

            {lOGstatus === "true" && (
                <div className="fd-profile">
                    <div className="fd-profile-shell">
                        <section className="fd-profile-user fd-glass-panel">
                            <div className="fd-profile-avatar-ring">
                                {profileImage ? (
                                    <div className="fd-profile-avatar" style={{ backgroundImage: `url(${profileImage})` }} />
                                ) : (
                                    <div className="fd-profile-avatar-placeholder">
                                        <User size={36} strokeWidth={1.8} />
                                    </div>
                                )}
                            </div>
                            <div className="fd-profile-user-info">
                                <div className="fd-profile-user-name">{nameI || t('Profile')}</div>
                                {phoneNumber && <div className="fd-profile-user-phone">{phoneNumber}</div>}
                                <div className="fd-profile-badge">
                                    <Star size={13} fill="currentColor" strokeWidth={0} />
                                    {t('Gold Member')}
                                </div>
                            </div>
                            <div className="fd-profile-actions">
                                <button
                                    className="fd-profile-edit"
                                    onClick={() => userCardNumber && setShowBarcodeModal(true)}
                                    aria-label={t('Show QR code') || 'Show QR code'}
                                >
                                    <QrCode size={18} />
                                </button>
                                <button className="fd-profile-edit" onClick={() => handleXBarItemChange(2)} aria-label={t('Edit profile') || 'Edit profile'}>
                                    <Edit3 size={18} />
                                </button>
                            </div>
                        </section>

                        <section className="fd-profile-summary">
                            <div className="fd-profile-summary-strip">
                                {loyaltySummaryKeys.map((key) => {
                                    const meta = RESTAURANT_META[key];
                                    if (!meta) return null;
                                    return (
                                        <div className="fd-profile-summary-card fd-glass-panel" key={key}>
                                            <div className="fd-profile-summary-logo">
                                                <img src={meta.logo} alt={meta.name} />
                                            </div>
                                            <div className="fd-profile-summary-name">{meta.name}</div>
                                            <div className="fd-profile-summary-points">{coins[key] || 0}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="fd-profile-summary-stack">
                                <div className="fd-profile-metric fd-glass-panel">
                                    <Truck size={20} />
                                    <div>
                                        <span>{t('Fast Delivery')}</span>
                                        <strong>{coins.fastdelivery || 0}</strong>
                                    </div>
                                </div>
                                <div className="fd-profile-metric fd-glass-panel fd-profile-metric--coins">
                                    <Star size={20} fill="currentColor" />
                                    <div>
                                        <span>{t('Total Art Coins')}</span>
                                        <strong>{loyaltyTotal}</strong>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div className="fd-profile-menu fd-glass-panel">
                            <div className="fd-profile-menu-item" onClick={() => setShowOrders(!showOrders)}>
                                <div className="fd-profile-menu-icon fd-profile-menu-icon--red">
                                    <History size={21} />
                                </div>
                                <span className="fd-profile-menu-label">{t('Order History')}</span>
                                <ChevronRight className={`fd-profile-menu-chevron ${showOrders ? 'fd-profile-menu-chevron--open' : ''}`} />
                            </div>

                            {showOrders && <div className="fd-profile-expanded"><OrderHistory /></div>}

                            <div className="fd-profile-menu-item">
                                <div className="fd-profile-menu-icon fd-profile-menu-icon--blue">
                                    <CreditCard size={21} />
                                </div>
                                <span className="fd-profile-menu-label">{t('Payment Methods')}</span>
                                <span className="fd-coming-soon-badge">{t('Coming Soon')}</span>
                            </div>

                            <div className="fd-profile-menu-item" onClick={() => setShowAddressManager(true)}>
                                <div className="fd-profile-menu-icon fd-profile-menu-icon--green">
                                    <MapPin size={21} />
                                </div>
                                <span className="fd-profile-menu-label">{t('Addresses')}</span>
                                <ChevronRight className="fd-profile-menu-chevron" />
                            </div>

                            <div className="fd-profile-menu-item" onClick={() => {
                                if (!showFavorites) {
                                    const user = getAuth().currentUser;
                                    if (user) {
                                        getDoc(doc(fs, 'users', user.uid)).then(snap => {
                                            setFavorites(snap.exists() ? (snap.data().favorites || []) : []);
                                        }).catch(() => {});
                                    }
                                }
                                setShowFavorites(p => !p);
                            }}>
                                <div className="fd-profile-menu-icon fd-profile-menu-icon--pink">
                                    <Heart size={21} />
                                </div>
                                <span className="fd-profile-menu-label">{t('Favorites')}</span>
                                <ChevronRight className={`fd-profile-menu-chevron ${showFavorites ? 'fd-profile-menu-chevron--open' : ''}`} />
                            </div>

                            {showFavorites && (
                                <div className="fd-favorites-list">
                                    {favorites.length === 0 ? (
                                        <div className="fd-favorites-empty">
                                            <Heart size={32} strokeWidth={1.5} />
                                            <span>{t('No favorites yet')}</span>
                                        </div>
                                    ) : (
                                        favorites.map(fav => (
                                            <div key={fav.id} className="fd-favorite-item">
                                                {fav.imageUrl ? (
                                                    <img src={fav.imageUrl} alt={fav.name} className="fd-favorite-img" />
                                                ) : (
                                                    <div className="fd-favorite-img fd-favorite-img--placeholder" />
                                                )}
                                                <div className="fd-favorite-info">
                                                    <span className="fd-favorite-name">{parseMultiLanguageDescription(fav.name, locale) || fav.name}</span>
                                                    <span className="fd-favorite-price">&#8382;{Number(fav.price).toFixed(2)}</span>
                                                </div>
                                                <button className="fd-favorite-remove" onClick={async () => {
                                                    const user = getAuth().currentUser;
                                                    if (!user) return;
                                                    const updated = favorites.filter(f => f.id !== fav.id);
                                                    await setDoc(doc(fs, 'users', user.uid), { favorites: updated }, { merge: true }).catch(() => {});
                                                    setFavorites(updated);
                                                }} aria-label={t('Remove') || 'Remove'}>
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            <div className="fd-profile-menu-item" onClick={() => handleXBarItemChange(4)}>
                                <div className="fd-profile-menu-icon fd-profile-menu-icon--gold">
                                    <BadgePercent size={21} />
                                </div>
                                <span className="fd-profile-menu-label">{t('Loyalty Program')}</span>
                                <ChevronRight className="fd-profile-menu-chevron" />
                            </div>

                            <div className="fd-profile-menu-item" onClick={() => {
                                if (!showSettings) {
                                    const user = getAuth().currentUser;
                                    if (user) {
                                        getDoc(doc(fs, 'users', user.uid)).then(snap => {
                                            if (snap.exists()) {
                                                const d = snap.data();
                                                const fullName = d.firstName && d.lastName ? `${d.firstName} ${d.lastName}` : d.name || nameI || '';
                                                setSettingsForm({
                                                    name: fullName,
                                                    phone: getUserPhoneNumber(d) || phoneNumber || '',
                                                    email: d.email || d.post || d.mail || postI || '',
                                                    personalId: d.personalId || '',
                                                });
                                                setMarketingOptIn(d.marketingOptIn === true);
                                            }
                                        }).catch(() => {});
                                    }
                                }
                                setShowSettings(p => !p);
                            }}>
                                <div className="fd-profile-menu-icon fd-profile-menu-icon--purple">
                                    <Settings size={21} />
                                </div>
                                <span className="fd-profile-menu-label">{t('Settings')}</span>
                                <ChevronRight className={`fd-profile-menu-chevron ${showSettings ? 'fd-profile-menu-chevron--open' : ''}`} />
                            </div>

                            {showSettings && (
                                <div className="fd-settings-section">
                                    {[
                                        { key: 'name', label: t('Full Name'), icon: '👤' },
                                        { key: 'phone', label: t('Phone Number'), icon: '📱' },
                                        { key: 'email', label: t('Email'), icon: '✉️' },
                                        { key: 'personalId', label: t('Personal Number'), icon: '🪪' },
                                    ].map(field => (
                                        <div key={field.key} className="fd-settings-field">
                                            <div className="fd-settings-field-icon">{field.icon}</div>
                                            <div className="fd-settings-field-body">
                                                <span className="fd-settings-field-label">{field.label}</span>
                                                <span className={`fd-settings-field-value${!settingsForm[field.key] ? ' fd-settings-field-value--empty' : ''}`}>
                                                    {settingsForm[field.key] || t('Not set')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="fd-settings-field">
                                        <div className="fd-settings-field-icon">🔔</div>
                                        <div className="fd-settings-field-body">
                                            <span className="fd-settings-field-label">{t('Marketing Notifications')}</span>
                                            <span className="fd-settings-field-value">{marketingOptIn ? t('Enabled') : t('Disabled')}</span>
                                        </div>
                                        <label className="fd-settings-toggle">
                                            <input type="checkbox" checked={marketingOptIn} onChange={handleMarketingToggle} />
                                            <span className="fd-settings-toggle-slider" />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div
                            className="fd-profile-balance"
                            onClick={() => userCardNumber && setShowBarcodeModal(true)}
                        >
                            <div className="fd-profile-balance-icon">
                                <Star size={26} fill="currentColor" />
                            </div>
                            <div className="fd-profile-balance-content">
                                <div className="fd-profile-balance-label">{t('TOTAL ACCUMULATED BALANCE')}</div>
                                <div className="fd-profile-balance-row">
                                    <span className="fd-profile-balance-amount">{totalCoins}</span>
                                    <span className="fd-profile-balance-unit">{t('Points')}</span>
                                </div>
                                <div className="fd-profile-balance-subtitle">
                                    {t('Use these points for free delivery or discounts')}
                                </div>
                            </div>
                            <button className="fd-profile-balance-cta" onClick={(e) => e.stopPropagation()}>
                                {t('View Rewards Catalog')}
                                <ArrowRight size={17} />
                            </button>
                        </div>

                        <div className="fd-profile-restaurants">
                            <div className="fd-profile-section-header">
                                <span className="fd-profile-section-title">{t('Points by Restaurant')}</span>
                                <span className="fd-profile-section-link">{t('See all')}</span>
                            </div>
                            <div className="fd-profile-restaurant-scroller">
                                {LOYALTY_RESTAURANTS.map((key) => {
                                    const meta = RESTAURANT_META[key];
                                    if (!meta) return null;
                                    const points = coins[key] || 0;
                                    const threshold = meta.rewardThreshold || 100;
                                    const progress = Math.min((points / threshold) * 100, 100);
                                    return (
                                        <div className="fd-profile-restaurant-row" key={key}>
                                            <div className="fd-profile-restaurant-top">
                                                <div className="fd-profile-restaurant-logo">
                                                    <img src={meta.logo} alt={meta.name} />
                                                </div>
                                                <div className="fd-profile-restaurant-points">
                                                    {points}
                                                    <span className="fd-profile-restaurant-pts-label"> {t('pts')}</span>
                                                </div>
                                            </div>
                                            <div className="fd-profile-restaurant-info">
                                                <div className="fd-profile-restaurant-name">{meta.name}</div>
                                                <div className="fd-profile-restaurant-progress">
                                                    <div
                                                        className="fd-profile-restaurant-progress-fill"
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                                <div className="fd-profile-restaurant-reward">
                                                    {t('Reward at')} {threshold} {t('pts')}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="fd-profile-logout">
                            <button className="fd-profile-logout-btn" onClick={handleOut}>
                                <LogOut size={18} />
                                {t('Log Out')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAddressManager && (
                <AddressManager onClose={() => setShowAddressManager(false)} />
            )}

            {showBarcodeModal && userCardNumber && (
                <div className="barcode-modal-overlay" onClick={() => setShowBarcodeModal(false)}>
                    <div className="barcode-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="barcode-modal-header">
                            <h2>{t('Loyalty Card') || 'Loyalty Card'}</h2>
                            <button className="barcode-modal-close" onClick={() => setShowBarcodeModal(false)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6l-12 12" />
                                    <path d="M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="barcode-modal-body">
                            <div className="barcode-wrapper">
                                <QRCodeSVG value={String(userCardNumber)} size={220} level="M" marginSize={2} />
                            </div>
                            <div className="barcode-wrapper">
                                <Barcode
                                    value={userCardNumber}
                                    format="CODE128"
                                    width={2}
                                    height={100}
                                    displayValue={true}
                                    fontSize={16}
                                    margin={10}
                                />
                            </div>
                            <div className="barcode-card-number">
                                {t('Card Number')}: {userCardNumber}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default UserPage;
